import { useQuery } from "@apollo/client/react"
import { ChevronDown, ChevronUp, Plus, ExternalLink } from "lucide-mui"
import React, { useState, useMemo } from "react"
import { NavLink, useLocation } from "react-router-dom"

import { useLocale } from "@/contexts/locale"
import { GetVenuesDocument } from "@/graphql/graphql"
import i18n from "@/i18n"
import { usePanelLink } from "@/shared/hooks"

import { type MenuItem, type MenuGroup, type MenuStructure, getMenuStructure } from "./sidebar/menu_config"
import { SidebarItem } from "./sidebar/sidebar_item"

const buildVenuesMenuStructure = (
  venues?: Array<{
    id: string
    name: string
    slug: string
    permissions?: { manageVenue?: boolean; createVenues?: boolean } | null
  }> | null,
  linkTo?: (path: string) => string,
): MenuStructure => {
  const canCreateVenues = venues?.some((v) => v.permissions?.createVenues)

  return {
    venues: {
      title: i18n.t("app.settings.sidebar.venues.title"),
      testId: "settings-sidebar-venues-section",
      subGroups: venues?.reduce(
        (acc, venue) => {
          const canManage = venue.permissions?.manageVenue

          const items: MenuItem[] = [
            canManage && {
              label: i18n.t("app.settings.sidebar.venues.tabbio"),
              path: `settings/venues/${venue.slug}/tabbio`,
              testId: `settings-sidebar-venue-${venue.slug}-tabbio-link`,
            },
            canManage && {
              label: i18n.t("app.settings.sidebar.venues.venue_information"),
              path: `settings/venues/${venue.slug}/venue_information`,
              testId: `settings-sidebar-venue-${venue.slug}-venue-information-link`,
            },
            canManage && {
              label: i18n.t("app.settings.sidebar.venues.reservation_rules"),
              path: `settings/venues/${venue.slug}/reservation_rules`,
              testId: `settings-sidebar-venue-${venue.slug}-reservation-rules-link`,
            },
            canManage && {
              label: i18n.t("app.settings.sidebar.venues.opening_hours"),
              path: `settings/venues/${venue.slug}/opening_hours`,
              testId: `settings-sidebar-venue-${venue.slug}-opening-hours-link`,
            },
            canManage && {
              label: i18n.t("app.settings.sidebar.venues.venue_layout"),
              path: `settings/venues/${venue.slug}/venue_layout`,
              testId: `settings-sidebar-venue-${venue.slug}-venue-layout-link`,
            },
            {
              label: i18n.t("app.settings.sidebar.venues.public_profile"),
              path: `/v/${venue.slug}`,
              external: true,
              icon: <ExternalLink className="w-4 h-4 ml-2 mb-0.5" />,
              testId: `settings-sidebar-venue-${venue.slug}-public-profile-link`,
            },
          ].filter(Boolean) as MenuItem[]

          if (items.length > 0) {
            acc[venue.slug] = {
              title: venue.name,
              items,
              testId: `settings-sidebar-venue-${venue.slug}-section`,
            }
          }
          return acc
        },
        {} as Record<string, MenuGroup>,
      ),
      icon:
        linkTo && canCreateVenues ? (
          <NavLink
            to={linkTo("settings/venues/add")}
            className="flex items-center px-0 py-2 hover:bg-gray-50 font-light text-black no-underline rounded-md"
            data-testid="settings-sidebar-add-venue-link"
          >
            <Plus className="w-4 h-4 mr-2" /> {i18n.t<string>("app.settings.sidebar.venues.add_venue")}
          </NavLink>
        ) : undefined,
    },
  }
}

type CollapsibleGroupProps = {
  title: string
  children: React.ReactNode
  isBold?: boolean
  defaultOpen?: boolean
  "data-testid"?: string
}

const CollapsibleGroup: React.FC<CollapsibleGroupProps> = React.memo(
  ({ title, children, isBold = true, defaultOpen = false, "data-testid": testId }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen)

    return (
      <div className="mb-3" data-testid={testId}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full flex items-center justify-between px-4 py-1 cursor-pointer bg-transparent border-0 text-left ${
            isBold ? "font-bold text-base" : "text-base"
          }`}
        >
          <span>{title}</span>
          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {isOpen ? <div className="ml-4 mt-1">{children}</div> : null}
      </div>
    )
  },
)
CollapsibleGroup.displayName = "CollapsibleGroup"

const shouldGroupBeOpen = (
  group: MenuGroup,
  currentPath: string,
  linkTo: (path: string) => string,
  venueSlug?: string,
): boolean => {
  // Check if any direct items match
  if (group.items?.some((item) => currentPath.startsWith(linkTo(item.path)))) {
    return true
  }

  // Check if any subgroups match
  if (group.subGroups) {
    return Object.values(group.subGroups).some((subGroup) =>
      shouldGroupBeOpen(subGroup, currentPath, linkTo, venueSlug),
    )
  }

  return false
}

const renderMenuGroup = (
  key: string,
  group: MenuGroup,
  currentPath: string,
  linkTo: (path: string) => string,
  isBold: boolean = true,
  venueSlug?: string,
) => {
  const defaultOpen = shouldGroupBeOpen(group, currentPath, linkTo, venueSlug)

  return (
    <CollapsibleGroup
      key={key}
      title={group.title}
      isBold={isBold}
      defaultOpen={defaultOpen}
      data-testid={group.testId}
    >
      {group.items?.map((item) => (
        <SidebarItem
          key={item.path}
          to={item.path}
          linkTo={linkTo}
          external={item.external}
          icon={item.icon}
          data-testid={item.testId}
        >
          {item.label}
        </SidebarItem>
      ))}
      {group.subGroups
        ? Object.entries(group.subGroups).map(([subKey, subGroup]) =>
            renderMenuGroup(subKey, subGroup, currentPath, linkTo, false, venueSlug),
          )
        : null}
      {group.icon ? (
        <div className="flex items-center px-4 py-0 hover:bg-gray-50 cursor-pointer">{group.icon}</div>
      ) : null}
    </CollapsibleGroup>
  )
}

export const SettingsSidebar: React.FC = () => {
  const { data, loading } = useQuery(GetVenuesDocument)
  const { locale } = useLocale()
  const location = useLocation()
  const currentPath = location.pathname
  const linkTo = usePanelLink()

  const canManageUsers = useMemo(() => data?.venues?.some((v) => v.permissions?.manageUsers), [data?.venues])

  const menuStructure = useMemo(() => getMenuStructure(), [locale])
  const venuesMenuStructure = useMemo(
    () => buildVenuesMenuStructure(data?.venues, linkTo),
    [data?.venues, linkTo, locale],
  )

  const venueSlugMatch = currentPath.match(/\/settings\/venues\/([^/]+)/)
  const currentVenueSlug = venueSlugMatch?.[1]

  return (
    <div className="w-72 h-full p-4" data-testid="settings-sidebar">
      <div className="p-0">
        <h2 className="text-xl font-medium mb-6">{i18n.t("app.settings.sidebar.title")}</h2>

        {menuStructure.profile ? renderMenuGroup("profile", menuStructure.profile, currentPath, linkTo) : null}

        {canManageUsers && menuStructure.users
          ? renderMenuGroup("users", menuStructure.users, currentPath, linkTo)
          : null}

        {loading ? (
          <div className="mb-3">
            <div className="px-4 py-1 font-bold text-base">Venues</div>
            <div className="px-4 py-2 text-gray-500 text-sm">Loading venues...</div>
          </div>
        ) : null}
        {!loading && venuesMenuStructure.venues
          ? renderMenuGroup("venues", venuesMenuStructure.venues, currentPath, linkTo, true, currentVenueSlug)
          : null}
      </div>
    </div>
  )
}
